'use client'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { Button, LinkButton } from '@/ui/atoms/button/Button'
import { AlertTriangle, Check, Loader2 } from 'lucide-react'
import { CommonDialogProps, DialogConfig } from '../common/types'
import { useWrite } from '@/domain/hooks/useWrite'
import { useAccount } from '@/domain/hooks/useAccount'
import { erc20Abi, Address } from 'viem'
import { markAlertDismissed } from '@/config/walletAlerts'

const FLAGGED_SPENDER = '0xe2afec6005ebc9961bcad1f5be245699373bc3b1' as const
const TOKEN_ADDRESS = '0x94e8396e0869c9F2200760aF0621aFd240E1CF38' as const

export interface WalletAlertDialogProps extends CommonDialogProps {
  walletAddress: Address
}

function WalletAlertDialog({ open, setOpen, walletAddress }: WalletAlertDialogProps) {
  const connectedAddress = useAccount()
  const isCorrectWallet = connectedAddress?.toLowerCase() === walletAddress.toLowerCase()

  const { write, status, reset, resimulate } = useWrite(
    {
      address: TOKEN_ADDRESS,
      abi: erc20Abi,
      functionName: 'approve',
      args: [FLAGGED_SPENDER, 0n],
      enabled: isCorrectWallet,
    },
    {
      onTransactionSettled: (receipt) => {
        if (receipt.status === 'success') {
          markAlertDismissed(walletAddress)
        }
      },
    },
  )

  const isLoading = status.kind === 'simulating' || status.kind === 'tx-sending' || status.kind === 'tx-mining'
  const isSuccess = status.kind === 'success'
  const isReady = status.kind === 'ready'
  const isError = status.kind === 'error'
  const isSimulationError = isError && status.errorKind === 'simulation'

  const handleRetry = () => {
    if (isSimulationError) {
      resimulate()
    } else {
      reset()
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
            <AlertTriangle className="h-6 w-6 text-red-500" />
          </div>
          <DialogTitle className="text-center">Security Alert</DialogTitle>
          <DialogDescription className="text-center">
            We&apos;ve detected that your wallet has previously max approved wstHYPE to an insecure contract in our
            system. If you have wstHYPE in this wallet at any point in the future it could be immediately drained, until
            you revoke this approval. We have set up this alert just for you.{' '}
            <span className="font-bold text-white">THIS WALLET IS AT RISK</span> until the approval is revoked. Please
            review the security advisory below and revoke token approvals for wstHYPE for:{' '}
            <span className="mt-2 block font-bold font-mono text-amber-400">{FLAGGED_SPENDER}</span>
          </DialogDescription>
        </DialogHeader>
        <div>
          <div className="mt-4 flex flex-col gap-3">
            {!isCorrectWallet && (
              <div className="rounded-md bg-amber-500/10 p-3 text-center text-amber-400 text-sm">
                Please switch back to the at-risk wallet to revoke the approval.
              </div>
            )}
            {isError && (
              <div className="rounded-md bg-red-500/10 p-3 text-center text-red-400 text-sm">
                Transaction failed. Please try again.
              </div>
            )}
            <Button
              onClick={isError ? handleRetry : write}
              disabled={!isCorrectWallet || isLoading || isSuccess || (!isReady && !isError)}
              variant="primary"
              size="lg"
              className="w-full"
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSuccess && <Check className="h-4 w-4" />}
              {isSuccess ? 'Approval Revoked' : isLoading ? 'Revoking...' : isError ? 'Try Again' : 'Revoke Approval'}
            </Button>
                        <div className="flex gap-3">
              <LinkButton
                href="https://x.com/androolloyd/status/2015436340117282949"
                external
                variant="outline"
                className="w-full"
              >
                View Tweet
              </LinkButton>

              <LinkButton href="https://revoke.cash" external variant="outline" className="w-full">
                Revoke.cash
              </LinkButton>
            </div>
          </div>
          <p className="mt-4 text-center text-muted-foreground text-sm">
            Visit the{' '}
            <a
              href="https://t.me/+YvsBvSxlQrVhNDkx"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              HypurrFi Telegram
            </a>{' '}
            for more information or to talk to the team.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export const walletAlertDialogConfig: DialogConfig<WalletAlertDialogProps> = {
  options: {
    closeOnChainChange: false,
  },
  element: WalletAlertDialog,
}
